import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from 'atoms/buttons/Button';
import { LoadingButton } from 'atoms/buttons/LoadingButton';
import { FontAwesomeIcon } from 'atoms/icons';
import { Horizontal, Vertical } from 'atoms/layout/flex';
import { BodyM, LabelL, LabelM } from 'atoms/typography';
import { Bold } from 'atoms/typography/Bold';
import { Dialog } from 'components/dialog';
import { DivisionLogo } from 'components/leaderboard/DivisionLogo';
import { WarningRow } from 'components/lineup/WarningRow';
import { glossary } from 'lib/glossary';
import { formatLineupDisplayName } from 'lib/so5';
import { tabletAndAbove } from 'style/mediaQuery';

import type { DropCardsWarningDialog_anyCardInterface } from './__generated__/index.graphql';

const Body = styled(Vertical).attrs({ gap: 5 })`
  padding: var(--double-unit) var(--double-unit) var(--quadruple-unit);
  @media ${tabletAndAbove} {
    min-width: 500px;
  }
`;

type Props = {
  open: boolean;
  onCancel: () => void;
  onContinue: () => void;
  cards: Array<DropCardsWarningDialog_anyCardInterface>;
  loading: boolean;
};

export const DropCardsWarningDialog = ({
  open,
  onCancel,
  onContinue,
  cards,
  loading,
}: Props) => {
  return (
    <Dialog
      maxWidth="sm"
      open={open}
      title={
        <Horizontal center>
          <FontAwesomeIcon
            icon={faExclamationCircle}
            color="var(--c-yellow-400)"
          />
          <LabelL bold className="text-center">
            <FormattedMessage
              id="DropCardsWarningDialog.dialogTitle"
              defaultMessage="Warning"
            />
          </LabelL>
        </Horizontal>
      }
      onClose={onCancel}
      footer={
        <Horizontal>
          <Button color="quaternary" size="medium" fullWidth onClick={onCancel}>
            <FormattedMessage {...glossary.cancel} />
          </Button>
          <LoadingButton
            color="red"
            size="medium"
            fullWidth
            onClick={onContinue}
            loading={loading}
          >
            <FormattedMessage {...glossary.confirm} />
          </LoadingButton>
        </Horizontal>
      }
    >
      <Body>
        <BodyM>
          <FormattedMessage
            id="DropCardsWarningDialog.description2"
            defaultMessage="Scrapping {count, plural, one {this Card} other {these Cards}} will remove {count, plural, one {it} other {them}} from {count, plural, one {its lineup} other {their lineups}}"
            values={{
              count: cards.length,
            }}
          />
        </BodyM>
        <Vertical gap={2}>
          {cards.map(card => {
            const usedLineup = card.liveSo5Lineup || card.openedSo5Lineups[0];
            if (!usedLineup) {
              return null;
            }
            return (
              <WarningRow
                key={card.slug}
                card={card}
                warning={
                  <>
                    {!!usedLineup.so5Leaderboard && (
                      <DivisionLogo
                        size={3}
                        so5Leaderboard={usedLineup.so5Leaderboard}
                      />
                    )}
                    <LabelM>
                      <FormattedMessage
                        id="WarningDialog.cardUsedInLineup"
                        defaultMessage="Used in <b>{lineupDisplayName}</b>"
                        values={{
                          lineupDisplayName:
                            formatLineupDisplayName(usedLineup),
                          b: Bold,
                        }}
                      />
                    </LabelM>
                  </>
                }
              />
            );
          })}
        </Vertical>
      </Body>
    </Dialog>
  );
};

DropCardsWarningDialog.fragments = {
  anyCardInterface: gql`
    fragment DropCardsWarningDialog_anyCardInterface on AnyCardInterface {
      slug
      openedSo5Lineups {
        id
        ...formatLineupDisplayName_so5Lineup
        so5Leaderboard {
          slug
          ...DivisionLogo_so5Leaderboard
        }
      }
      liveSo5Lineup {
        id
        ...formatLineupDisplayName_so5Lineup
        so5Leaderboard {
          slug
          ...DivisionLogo_so5Leaderboard
        }
      }
      ...WarningRow_anyCard
    }
    ${WarningRow.fragments.anyCard}
    ${formatLineupDisplayName.fragments.so5Lineup}
    ${DivisionLogo.fragments.so5Leaderboard}
  ` as TypedDocumentNode<DropCardsWarningDialog_anyCardInterface>,
};
